import { type APIClientDTO, type CompanyListing, UpdateAPIClientAccessDTO } from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { app, router } from "../../init";
import { config, shared } from "../../styles";
import { alert, confirm } from "../alert-dialog";
import "../popover";
import type { Popover } from "../popover";
import "../scroller";
import { grantableCompanies } from "./api-client.util";

@customElement("ptc-admin-api-client")
export class AdminAPIClient extends LitElement {
    @property({ attribute: false })
    client: APIClientDTO | null = null;

    @property({ attribute: false })
    companies: CompanyListing[] = [];

    @state()
    private _loading = false;

    @state()
    private _companyQuery = "";

    @query("#grantPopover")
    private _grantPopover: Popover;

    private async _copyClientId() {
        if (!this.client) return;
        await navigator.clipboard.writeText(this.client.id);
    }

    private async _grant(company: CompanyListing) {
        if (!this.client) return;
        this._grantPopover.hide();
        await this._run(() =>
            app.api.grantAPIClientAccess(
                new UpdateAPIClientAccessDTO({ clientId: this.client!.id, companyId: company.id })
            )
        );
    }

    private async _revoke(access: APIClientDTO["accesses"][number]) {
        if (!this.client) return;
        const confirmed = await confirm(
            html`Möchten Sie den Zugriff von <strong>${this.client.name}</strong> auf
                <strong>${access.companyName}</strong> wirklich entziehen? Bereits ausgestellte Tokens für dieses
                Unternehmen werden dadurch ungültig.`,
            "Zugriff entziehen",
            "Abbrechen",
            { type: "destructive" }
        );
        if (!confirmed) return;
        await this._run(() =>
            app.api.revokeAPIClientAccess(
                new UpdateAPIClientAccessDTO({ clientId: this.client!.id, companyId: access.companyId })
            )
        );
    }

    private async _run(action: () => Promise<void>) {
        this._loading = true;
        try {
            await action();
            this.dispatchEvent(new CustomEvent("changed"));
        } catch (e) {
            await alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    static styles = [
        config,
        shared,
        css`
            :host {
                position: relative;
            }

            input[readonly] {
                border: none;
                width: 100%;
                padding-inline: 0.5em;
            }

            .company-picker {
                width: 24em;
            }

            .company-picker .input {
                margin: 0.5em;
            }

            .company-list {
                max-height: 30em;
            }

            .company-option {
                width: 100%;
                display: block;
                overflow: hidden;
            }

            .revoke {
                color: var(--color-negative);
            }
        `,
    ];

    private _renderGrantPopover(client: APIClientDTO) {
        const candidates = grantableCompanies(this.companies, client, this._companyQuery);
        return html`
            <ptc-popover id="grantPopover" class="company-picker" @popover-hide=${() => (this._companyQuery = "")}>
                <div class="vertical layout">
                    <div class="small right icon input">
                        <input
                            placeholder="Unternehmen suchen..."
                            .value=${live(this._companyQuery)}
                            @input=${(e: InputEvent) => (this._companyQuery = (e.target as HTMLInputElement).value)}
                        />
                        <ptc-ui-icon name="icon.action.search" class="icon"></ptc-ui-icon>
                    </div>
                    <ptc-scroller class="company-list">
                        ${candidates.length
                            ? candidates.map(
                                  (company) => html`
                                      <button
                                          class="transparent slim company-option"
                                          @click=${() => this._grant(company)}
                                      >
                                          <div class="ellipsis text-left-aligning">
                                              ${company.name} <span class="subtle">(${company.id})</span>
                                          </div>
                                      </button>
                                  `
                              )
                            : html`<div class="padded subtle">Keine passenden Unternehmen gefunden.</div>`}
                    </ptc-scroller>
                </div>
            </ptc-popover>
        `;
    }

    render() {
        const client = this.client;

        if (!client) {
            return html`
                <div class="fullbleed centering vertical subtle layout">
                    <ptc-ui-icon name="icon.object.api-source" class="giant"></ptc-ui-icon>
                    <div>Kein API-Client gewählt.</div>
                </div>
            `;
        }

        return html`
            <ptc-scroller class="fullbleed">
                <div class="padded narrow-content">
                    <div class="double-padded">
                        <div class="margined uppercase">Client-Daten</div>

                        <div class="field">
                            <label>Client-ID</label>
                            <div class="horizontal center-aligning layout">
                                <input readonly class="stretch" .value=${live(client.id)} />
                                <button
                                    class="slim transparent"
                                    title="Client-ID kopieren"
                                    @click=${this._copyClientId}
                                >
                                    <ptc-ui-icon name="icon.action.copy-to-clipboard"></ptc-ui-icon>
                                </button>
                            </div>
                        </div>

                        <div class="field">
                            <label>Name</label>
                            <input readonly .value=${live(client.name)} />
                        </div>
                        <div class="field">
                            <label>E-Mail</label>
                            <input readonly .value=${live(client.email)} />
                        </div>
                        <div class="field">
                            <label>Website</label>
                            <input readonly .value=${live(client.website)} />
                        </div>

                        ${client.partnerScopes.length
                            ? html`
                                  <div class="field">
                                      <label>Partner-Scopes</label>
                                      <div class="horizontal spacing wrapping layout">
                                          ${client.partnerScopes.map((scope) => html`<div class="pill">${scope}</div>`)}
                                      </div>
                                  </div>
                              `
                            : nothing}
                    </div>

                    <div class="double-padded border-top">
                        <div class="margined horizontal center-aligning layout">
                            <div class="stretch uppercase">
                                Zugriff: <strong>${client.accesses.length}</strong> Unternehmen
                            </div>
                            <button class="small slim ghost" ?disabled=${this._loading}>
                                <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                                Zugriff hinzufügen
                            </button>
                            ${this._renderGrantPopover(client)}
                        </div>

                        ${client.accesses.length
                            ? html`
                                  <div class="smaller vertical spacing layout">
                                      ${client.accesses.map(
                                          (access) => html`
                                              <div class="horizontal center-aligning layout padded box">
                                                  <div class="stretch ellipsis">
                                                      ${access.companyName}
                                                      <span class="subtle">(${access.companyId})</span>
                                                  </div>
                                                  <button
                                                      class="slim transparent"
                                                      title="Unternehmen öffnen"
                                                      @click=${() => router.go(`companies/${access.companyId}`)}
                                                  >
                                                      <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                                                  </button>
                                                  <button
                                                      class="slim transparent revoke"
                                                      title="Zugriff entziehen"
                                                      ?disabled=${this._loading}
                                                      @click=${() => this._revoke(access)}
                                                  >
                                                      <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                                  </button>
                                              </div>
                                          `
                                      )}
                                  </div>
                              `
                            : html`
                                  <div class="subtle margined">
                                      Dieser Client hat noch keinen Zugriff auf Unternehmen. Mit
                                      <strong>Zugriff hinzufügen</strong> können Sie ein Unternehmen freigeben.
                                  </div>
                              `}
                    </div>
                </div>
            </ptc-scroller>

            ${this._loading
                ? html`
                      <div class="fullbleed center-aligning center-justifying vertical layout scrim">
                          <ptc-ui-spinner active></ptc-ui-spinner>
                      </div>
                  `
                : nothing}
        `;
    }
}
